import React, { createContext, useEffect, useReducer } from 'react'
import axios from '../../axios.js'
import MatxLoading from '../components/MatxLoading/MatxLoading'
import { API_URI } from '../../config'

const initialState = {
    isAuthenticated: false,
    isInitialised: false,
    user: null,
}

const isUsableToken = (token) => {
    if (!token || token === 'undefined' || token === 'null') return false
    try {
        const parts = token.split('.')
        if (parts.length < 2) return false
        const payload = JSON.parse(atob(parts[1]))
        if (payload?.exp && payload.exp * 1000 <= Date.now()) {
            return false
        }
        return true
    } catch (e) {
        return false
    }
}

const setSession = (accessToken) => {
    if (isUsableToken(accessToken)) {
        localStorage.setItem('accessToken', accessToken)
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`
    } else {
        localStorage.removeItem('accessToken')
        delete axios.defaults.headers.common.Authorization
    }
}

const isUnauthorizedError = (err) => {
    if (!err || typeof err !== 'object') return false
    if (err.status === 401) return true
    const message = `${err.error || ''} ${err.message || ''}`.toLowerCase()
    return (
        message.includes('unauthorised') ||
        message.includes('unauthorized') ||
        message.includes('you must be logged in')
    )
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT': {
            const { isAuthenticated, user, preserveUser } = action.payload
            return {
                ...state,
                isAuthenticated,
                isInitialised: true,
                user: preserveUser ? state.user : user,
            }
        }
        case 'LOGIN': {
            const { user } = action.payload
            return {
                ...state,
                isAuthenticated: true,
                isInitialised: true,
                user,
            }
        }
        case 'LOGOUT': {
            return {
                ...state,
                isAuthenticated: false,
                user: null,
            }
        }
        case 'REGISTER': {
            const { user } = action.payload
            return {
                ...state,
                isAuthenticated: true,
                isInitialised: true,
                user,
            }
        }
        default:
            return state
    }
}

const AuthContext = createContext({
    ...initialState,
    method: 'JWT',
    login: () => Promise.resolve(),
    logout: () => {},
    register: () => Promise.resolve(),
})

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const login = async (email, mfaCode) => {
        const response = await axios.post(
            `${API_URI}/api/admin/auth/vefify-mfa`,
            {
                email,
                mfaCode,
            }
        )

        const token = response?.data?.token
        const user = response?.data?.user

        if (!isUsableToken(token) || !user) {
            throw {
                error: 'Login succeeded but no valid token was returned.',
                status: 500,
            }
        }

        window.localStorage.setItem('ROLE', user.role)
        setSession(token)

        dispatch({
            type: 'LOGIN',
            payload: { user },
        })

        return user
    }

    const register = async (email, password) => {
        const response = await axios.post(
            `${API_URI}/api/admin/auth/register`,
            {
                email,
                password,
            }
        )

        const { accessToken, user } = response.data
        setSession(accessToken)

        dispatch({
            type: 'REGISTER',
            payload: { user },
        })

        return user
    }

    const logout = () => {
        window.localStorage.removeItem('ROLE')
        setSession(null)
        dispatch({ type: 'LOGOUT' })
    }

    useEffect(() => {
        let cancelled = false

        ;(async () => {
            const accessToken = window.localStorage.getItem('accessToken')

            if (!isUsableToken(accessToken)) {
                setSession(null)
                if (!cancelled) {
                    dispatch({
                        type: 'INIT',
                        payload: {
                            isAuthenticated: false,
                            user: null,
                        },
                    })
                }
                return
            }

            setSession(accessToken)

            try {
                const { data } = await axios.get(`${API_URI}/api/admin/me`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                })

                if (cancelled) return

                const latestToken = window.localStorage.getItem('accessToken')
                if (latestToken && latestToken !== accessToken) {
                    dispatch({
                        type: 'INIT',
                        payload: {
                            isAuthenticated: true,
                            user: null,
                            preserveUser: true,
                        },
                    })
                    return
                }

                if (data?.role) {
                    window.localStorage.setItem('ROLE', data.role)
                }

                dispatch({
                    type: 'INIT',
                    payload: {
                        isAuthenticated: true,
                        user: data || null,
                    },
                })
            } catch (err) {
                if (cancelled) return

                const latestToken = window.localStorage.getItem('accessToken')
                if (latestToken && latestToken !== accessToken) {
                    dispatch({
                        type: 'INIT',
                        payload: {
                            isAuthenticated: true,
                            user: null,
                            preserveUser: true,
                        },
                    })
                    return
                }

                if (isUnauthorizedError(err)) {
                    setSession(null)
                    window.localStorage.removeItem('ROLE')
                    dispatch({
                        type: 'INIT',
                        payload: {
                            isAuthenticated: false,
                            user: null,
                        },
                    })
                    return
                }

                // Soft failure: keep session if token is still structurally valid
                dispatch({
                    type: 'INIT',
                    payload: {
                        isAuthenticated: true,
                        user: null,
                    },
                })
            }
        })()

        return () => {
            cancelled = true
        }
    }, [])

    if (!state.isInitialised) {
        return <MatxLoading />
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'JWT',
                login,
                logout,
                register,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export default AuthContext
